import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Lock, ShieldCheck } from 'lucide-react';
import api from '../services/api';

const ORANGE = '#FF5E00';
const GOLD = '#C9A961';
const GRADES = ['K', '1', '2', '3', '4', '5', '6', '7', '8'];
const inputCls = 'w-full !bg-black !text-white border-2 border-white/10 focus:border-[#FF5E00] outline-none px-4 py-3 text-sm text-white placeholder:!text-gray-600 transition-colors';

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[10px] font-black tracking-[0.25em] uppercase text-gray-400 mb-2">{label}</span>
    {children}
  </label>
);

const Toggle = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-2" data-testid="prereg-reclassing">
    {[['Yes', true], ['No', false]].map(([l, v]) => (
      <button type="button" key={l} onClick={() => onChange(v)} className={`py-3 text-xs font-black tracking-[0.2em] uppercase border-2 transition-colors ${value === v ? 'text-black border-[#FF5E00]' : 'text-gray-400 border-white/10 hover:border-white/30'}`} style={value === v ? { backgroundColor: ORANGE } : {}} data-testid={`prereg-reclassing-${l.toLowerCase()}`}>{l}</button>
    ))}
  </div>
);

const SeatsLeft = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get('/preregister/seats').then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const pct = Math.min(100, (100 * s.reserved) / s.total);
  return (
    <div className="mt-6 border-2 p-5" style={{ borderColor: `${ORANGE}60`, backgroundColor: `${ORANGE}0D` }} data-testid="prereg-seats">
      <div className="flex items-center gap-2">
        <span className="relative flex w-2.5 h-2.5"><span className="absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping" style={{ backgroundColor: ORANGE }} /><span className="relative inline-flex w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ORANGE }} /></span>
        <span className="text-lg font-black uppercase tracking-tight" data-testid="prereg-seats-left">{s.left > 0 ? `Only ${s.left} seats left for 2027–28` : 'Waitlist only for 2027–28'}</span>
      </div>
      <div className="h-2 bg-white/10 mt-4 overflow-hidden"><div className="h-full transition-all duration-1000" style={{ width: `${pct}%`, backgroundColor: ORANGE }} /></div>
      <div className="flex justify-between text-[10px] font-black tracking-[0.2em] uppercase text-gray-400 mt-2">
        <span data-testid="prereg-seats-reserved">{s.reserved} reserved</span><span data-testid="prereg-seats-total">{s.total} seats total</span>
      </div>
    </div>
  );
};

const EMPTY = { parent_name: '', student_name: '', grade: '', current_school: '', reclassing: null, sport: '', email: '', phone: '' };

export default function PreRegister() {
  const [params] = useSearchParams();
  const [f, setF] = useState(EMPTY);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(params.get('canceled') ? 'Payment was canceled. Your spot is not held until the fee is paid.' : '');
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!f.grade || f.reclassing === null) return setErr('Please choose a grade and answer the reclassing question.');
    if (!agree) return setErr('Please confirm the $650 registration fee is non-refundable.');
    setBusy(true);
    try {
      const r = await api.post('/preregister/checkout', { ...f, agree_nonrefundable: agree, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setErr(Array.isArray(d) ? 'Please check every field (valid email and phone).' : d || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#050508' }} data-testid="preregister-page">
      <div className="max-w-5xl mx-auto px-5 pt-16 pb-20 grid lg:grid-cols-[1fr_1.3fr] gap-12">
        <div>
          <div className="text-[10px] font-black tracking-[0.35em] uppercase mb-3" style={{ color: GOLD }}>2027–28 School Year · K–8</div>
          <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tight leading-[0.95]">Pre-Register.<br /><span style={{ color: ORANGE }}>Hold the Seat.</span></h1>
          <p className="text-gray-400 text-sm mt-6 leading-relaxed">Seats for next fall are limited. Pre-registering with the registration fee covers your student's technology and guarantees their spot for 2027–28.</p>
          <SeatsLeft />
          <div className="mt-6 border-2 p-5" style={{ borderColor: `${GOLD}40` }}>
            <div className="text-[10px] font-black tracking-[0.25em] uppercase text-gray-500">Registration Fee</div>
            <div className="text-4xl font-black mt-1" data-testid="prereg-fee">$650</div>
            <div className="text-[11px] text-gray-400 mt-2">Non-refundable · Technology + seat hold · Separate from tuition</div>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-5" data-testid="preregister-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Parent Name"><input required className={inputCls} value={f.parent_name} onChange={set('parent_name')} data-testid="prereg-parent-name" /></Field>
            <Field label="Student Name"><input required className={inputCls} value={f.student_name} onChange={set('student_name')} data-testid="prereg-student-name" /></Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Grade in 2027–28">
              <select required className={inputCls} value={f.grade} onChange={set('grade')} data-testid="prereg-grade">
                <option value="">Select grade</option>
                {GRADES.map((g) => <option key={g} value={g}>{g === 'K' ? 'Kindergarten' : `Grade ${g}`}</option>)}
              </select>
            </Field>
            <Field label="Sport"><input required className={inputCls} value={f.sport} onChange={set('sport')} placeholder="e.g. Basketball" data-testid="prereg-sport" /></Field>
          </div>
          <Field label="Current School"><input required className={inputCls} value={f.current_school} onChange={set('current_school')} data-testid="prereg-current-school" /></Field>
          <Field label="Reclassing?"><Toggle value={f.reclassing} onChange={(v) => setF({ ...f, reclassing: v })} /></Field>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Email"><input required type="email" className={inputCls} value={f.email} onChange={set('email')} data-testid="prereg-email" /></Field>
            <Field label="Phone"><input required type="tel" className={inputCls} value={f.phone} onChange={set('phone')} data-testid="prereg-phone" /></Field>
          </div>
          <label className="flex items-start gap-3 cursor-pointer" data-testid="prereg-agree-label">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 w-4 h-4 accent-[#FF5E00]" data-testid="prereg-agree" />
            <span className="text-xs text-gray-300 leading-relaxed">I understand the <strong className="text-white">$650 registration fee is non-refundable</strong>. It covers technology and holds my student's seat for 2027–28, and is separate from tuition.</span>
          </label>
          {err && <div className="text-xs text-red-400 border-2 border-red-500/30 bg-red-500/5 px-4 py-3" data-testid="prereg-error">{err}</div>}
          <button type="submit" disabled={busy} className="w-full flex items-center justify-center gap-2 text-black font-black text-xs tracking-[0.25em] uppercase py-4 transition-transform hover:-translate-y-0.5 disabled:opacity-60" style={{ backgroundColor: ORANGE }} data-testid="prereg-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Continue to Secure Payment · $650
          </button>
          <div className="flex items-center justify-center gap-2 text-[10px] text-gray-500 uppercase tracking-widest"><ShieldCheck className="w-3.5 h-3.5" /> Secure checkout by Stripe</div>
        </form>
      </div>
    </div>
  );
}
